'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ActiveFieldPresence {
  formId: string;
  fieldName: string;
}

export interface FormPresence {
  userId: string;
  userName: string;
  activeField: ActiveFieldPresence | null;
  updatedAt: number;
}

interface CollaborationEvent {
  type: 'presence' | 'storage' | 'submit';
  roomId: string;
  userId: string;
  payload: unknown;
}

interface FormStorageState {
  values: Record<string, unknown>;
  updatedAt: number;
  updatedBy: string;
}

const CHANNEL_NAME = 'runtime-shell-liveblocks-lite';
const PRESENCE_TTL_MS = 30000;

function getStorageKey(roomId: string, key: 'presence' | 'storage'): string {
  return `liveblocks-lite:${roomId}:${key}`;
}

function now(): number {
  return Date.now();
}

function readPresence(roomId: string): Record<string, FormPresence> {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(getStorageKey(roomId, 'presence'));
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, FormPresence>;
    const cutoff = now() - PRESENCE_TTL_MS;
    return Object.fromEntries(
      Object.entries(parsed).filter(([, presence]) => presence.updatedAt > cutoff),
    );
  } catch {
    return {};
  }
}

function writePresence(roomId: string, presenceByUser: Record<string, FormPresence>): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(getStorageKey(roomId, 'presence'), JSON.stringify(presenceByUser));
}

function readStorage(roomId: string): FormStorageState | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(getStorageKey(roomId, 'storage'));
  if (!raw) return null;

  try {
    return JSON.parse(raw) as FormStorageState;
  } catch {
    return null;
  }
}

function writeStorage(roomId: string, value: FormStorageState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(getStorageKey(roomId, 'storage'), JSON.stringify(value));
}

function publish(event: CollaborationEvent): void {
  if (typeof window === 'undefined') return;

  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage(event);
  channel.close();
}

export function useLiveblocksLite(roomId: string, userName: string) {
  const [myUserId] = useState(() => {
    if (typeof window === 'undefined') return 'server-user';
    const existing = window.sessionStorage.getItem('liveblocks-lite:user-id');
    if (existing) return existing;
    const next = `user-${Math.random().toString(36).slice(2, 11)}`;
    window.sessionStorage.setItem('liveblocks-lite:user-id', next);
    return next;
  });

  const [presenceByUser, setPresenceByUser] = useState<Record<string, FormPresence>>(() => readPresence(roomId));
  const [formStorage, setFormStorage] = useState<FormStorageState | null>(() => readStorage(roomId));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    const onMessage = (messageEvent: MessageEvent<CollaborationEvent>) => {
      const event = messageEvent.data;
      if (!event || event.roomId !== roomId || event.userId === myUserId) {
        return;
      }

      if (event.type === 'presence') {
        setPresenceByUser(readPresence(roomId));
      }
      if (event.type === 'storage') {
        setFormStorage(readStorage(roomId));
      }
    };

    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [myUserId, roomId]);

  const updateMyPresence = useCallback((activeField: ActiveFieldPresence | null) => {
    const current = readPresence(roomId);
    current[myUserId] = {
      userId: myUserId,
      userName,
      activeField,
      updatedAt: now(),
    };
    writePresence(roomId, current);
    setPresenceByUser(current);

    publish({
      type: 'presence',
      roomId,
      userId: myUserId,
      payload: current[myUserId],
    });
  }, [myUserId, roomId, userName]);

  const broadcastEvent = useCallback((payload: unknown) => {
    publish({
      type: 'submit',
      roomId,
      userId: myUserId,
      payload,
    });
  }, [myUserId, roomId]);

  const onEvent = useCallback((listener: (event: CollaborationEvent) => void) => {
    if (typeof window === 'undefined') return () => undefined;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    const onMessage = (messageEvent: MessageEvent<CollaborationEvent>) => {
      const event = messageEvent.data;
      if (!event || event.roomId !== roomId || event.userId === myUserId) {
        return;
      }
      listener(event);
    };
    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [myUserId, roomId]);

  const persistStorage = useCallback((values: Record<string, unknown>) => {
    const next: FormStorageState = {
      values,
      updatedAt: now(),
      updatedBy: myUserId,
    };
    writeStorage(roomId, next);
    setFormStorage(next);
    publish({
      type: 'storage',
      roomId,
      userId: myUserId,
      payload: next,
    });
  }, [myUserId, roomId]);

  const others = useMemo(
    () => (Object.values(presenceByUser) as FormPresence[]).filter(user => user.userId !== myUserId),
    [myUserId, presenceByUser],
  );

  return {
    myUserId,
    others,
    updateMyPresence,
    broadcastEvent,
    onEvent,
    storage: formStorage,
    persistStorage,
  };
}
